import MediaAccordion, { MediaAccordionProps } from '@/components/_organisms/MediaAccordion';
import Image from '@/components/Image';
import InlineMutedVideo from '@/components/InlineMutedVideo';
import { ModuleMediaAccordionFragment } from '@/graphql';
import { getSingleLink } from '@/lib/craft-utils';
import { parseHtml } from '@/lib/parse-html';
import { getSingle, notEmpty } from '@/lib/typescript-helpers';
import { FC } from 'react';

type ComponentProps = Pick<MediaAccordionProps, 'onChangeActiveIndex' | 'eventCategory' | 'activeIndex'>;

interface MediaAccordionModuleProps extends PropsWithClassName, ComponentProps {
  props: CraftModuleFragment<ModuleMediaAccordionFragment>;
}

const MediaAccordionModule: FC<MediaAccordionModuleProps> = ({ props, className, ...moduleProps }) => {
  if (Array.isArray(props)) {
    props = props[0];
  }

  if (!props) return null;

  return (
    <MediaAccordion
      {...moduleProps}
      className={className}
      heading={props.heading ?? undefined}
      description={parseHtml(props.description) ?? undefined}
      items={(props.items ?? [])
        .filter(notEmpty)
        .map((item, index) => {
          const asset = getSingle(item.asset);

          if (!asset) return null;

          const isVideo = asset.mimeType?.startsWith('video') ?? false;
          const singleCta = getSingleLink(item.cta);

          return {
            id: item?.id ?? undefined,
            title: item?.heading ?? '',
            content: parseHtml(item?.text),
            media: isVideo ? (
              <InlineMutedVideo
                loop
                autoPlay
                muted
                playsInline
                $lazy={index === 0}
                width={asset.width ?? undefined}
                height={asset.height ?? undefined}
              >
                <source src={asset.url!} />
              </InlineMutedVideo>
            ) : (
              <Image src={asset.url!} width={asset.width!} height={asset.height!} alt={asset.altText ?? ''} />
            ),
            autoAdvanceSeconds: isVideo ? asset.videoDuration : undefined,
            cta: singleCta
              ? {
                href: singleCta.url,
                text: singleCta.text,
              }
              : undefined,
          } satisfies MediaAccordionProps['items'][0];
        })
        .filter(notEmpty)}
    />
  );
};

export default MediaAccordionModule;
